import { IElementLayout } from '../Element';
import UIItem, { IImageData, ITextData } from '../UIItem';
import GameObject, { GameObjectEvent } from '../../../GameObject/GameObject';
import { StartAnimation } from '../../../System/Animation/AnimationHelper';
import ElementComponent from '../../../Component/UI/Element';
import TransformComponent from '../../../Component/Transform';
import Renderable from '../../../Component/Renderable';
import Interactable from '../../../Component/Interactable';
import AnimationComponent from '../../../Component/Animation';
import { IButtonData } from '../BaseElements/Button';
import Point from '../../../Utility/Point';
import Game from '../../../Game';
import ColorFilter from '../../../Component/ColorFilter';
import { BoosterType } from '../../../State/Card/BoosterType';
import UITextComponent from '../../../Component/UI/Text';
import { FTUXType } from '../../../Player/PlayerProperty/Progress/FTUXType';
import { LevelComponent } from '../../../State/Level/LevelComponent';
import { TextAlignment } from '../TextAlignment';
import { FTUXStateHelper } from 'State/Game/FTUX/FTUXStateHelper';

interface IToolboxItemData {
    type: BoosterType;
    componentType?: LevelComponent;
    layout?: IElementLayout;
    onClick: () => any;
    [key: string]: any;
}

export default class ToolboxItem extends GameObject {
    private _FTUX_Type: FTUXType;
    private _boosterType: BoosterType;

    public onCreate(params: IToolboxItemData): void {
        this.name = `booster_${params.type}`;
        this._boosterType = params.type;
        this._FTUX_Type = params.FTUX;

        this.iconParams.cacheKey = `booster_icon_${params.type.toLowerCase()}`;
        this.titleParams.textLocalizationKey = params.type;
        this.descriptionParams.textLocalizationKey = `BoosterDesc_${params.type}`;

        this.addComponent<TransformComponent>(TransformComponent, params);
        this.addComponent<Renderable>(Renderable, params);
        this.addComponent<Interactable>(Interactable, { buttonMode: true });
        this.addComponent<AnimationComponent>(AnimationComponent, params);
        this.addComponent<ColorFilter>(ColorFilter, params);
        this.on(GameObjectEvent.POINTER_DOWN, () => {
            this.enabled = false;
            params.onClick();
        });

        const iconBackground: GameObject<PIXI.Container> = UIItem.createImage(this.iconBackgroundParams);

        this.container.hitArea = new PIXI.Rectangle(-100, -90, 180, 180);
        const descriptionBackground: GameObject<PIXI.Container> = UIItem.createImage(this.descriptionContainer);
        const icon: GameObject<PIXI.Container> = UIItem.createImage(this.iconParams);
        const cost: GameObject<PIXI.Container> = UIItem.createText(this.costParams);
        const coin_icon: GameObject<PIXI.Container> = UIItem.createImage(this.coinIconParams);
        const title: GameObject<PIXI.Container> = UIItem.createText(this.titleParams);
        const description: GameObject<PIXI.Container> = UIItem.createText(this.descriptionParams);

        iconBackground.addChild(icon);
        iconBackground.addChild(coin_icon);
        coin_icon.addChild(cost);
        descriptionBackground.addChild(title);
        descriptionBackground.addChild(description);

        this.addChild(descriptionBackground);
        this.addChild(iconBackground);
    }
    public hideDescription(): void {
        this.descriptionSection.getComponent(ElementComponent).scale.x = 0;
    }
    public showDescription(): void {
        StartAnimation(this, 'Scale', {
            startScale: new Point(0, 1),
        });
    }
    public get descriptionSection(): GameObject {
        return this.find(this.descriptionContainer.name);
    }
    public get title(): GameObject {
        return this.find(this.descriptionContainer.name).find(this.titleParams.name);
    }
    public get description(): GameObject {
        return this.find(this.descriptionContainer.name).find(this.descriptionParams.name);
    }
    public set cost(value: string) {
        this.find(this.iconBackgroundParams.name).getComponentsInChildren(UITextComponent)[0].text = value;
    }
    public get boosterType(): BoosterType {
        return this._boosterType;
    }
    public get FTUX_Type(): FTUXType {
        return this._FTUX_Type;
    }
    public get isUnlocked(): boolean {
        return FTUXStateHelper.isFtuxCompleted(Game.store.getState().playerState, this.FTUX_Type);
    }

    public set enabled(value: boolean) {
        this.getComponent(ColorFilter).saturation = Number(value);
        this.getComponent(Interactable).enabled = value;
    }

    // Params ------------------------------------------------
    protected iconBackgroundParams: IButtonData = {
        name: 'icon_container',
        cacheKey: 'boosteritem_bg',
        layout: {
            anchors: [0, 0, 0, 0],
            pivot: [0.6, 0.5],
            offsets: [0, 0, 0, 0],
            scale: [1, 1],
        },
    };
    protected costParams: ITextData = {
        name: 'booster_cost',
        layout: {
            offsets: [0, 0, 0, 0],
            anchors: [1, 0.5, 1, 0.5],
            pivot: [0, 0.5],
        },
        text: '999',
        style: {
            ...Game.style.getStyle('HUD_in_game.buttons'),
            font: 'FrancoisOne',
            fontSize: '36px',
        },
        bounds: [100, 50],
    };
    protected coinIconParams: IImageData = {
        name: 'coin_icon',
        cacheKey: 'level_coin_medium',
        layout: {
            anchors: [0.42, 0.71, 0.42, 0.71],
            pivot: [1, 0.5],
            offsets: [0, 0, 0, 0],
            scale: [0.8, 0.8],
        },
    };
    protected iconParams: IImageData = {
        name: 'icon',
        cacheKey: '',
        layout: {
            anchors: [0.5, 0.34, 0.5, 0.34],
            pivot: [0.5, 0.5],
            offsets: [0, 0, 0, 0],
            scale: [1, 1],
        },
    };
    protected titleParams: ITextData = {
        name: 'title',
        textLocalizationKey: '',
        layout: {
            anchors: [0.5, 0.23, 0.5, 0.23],
            pivot: [0.5, 0.5],
            offsets: [0, 0, 0, 0],
            scale: [1, 1],
        },
        bounds: [180, 70],
        style: {
            ...Game.style.getStyle('HUD_in_game.buttons'),
            font: 'FrancoisOne',
            fontSize: '36px',
        },
        align: TextAlignment.CENTER,
    };
    protected descriptionBackgroundParams: any = {
        type: 'group',
        name: '',
        layout: {
            anchors: [0, 0, 0, 0],
            pivot: [0.5, 0.5],
            offsets: [0, 30, 0, 30],
            constrains: {
                heightMin: 137,
                heightMax: 137,
                widthMin: 233,
                widthMax: 233,
            },
        },
        components: [
            {
                type: 'image',
                data: {
                    cacheKey: 'boosteritem_desc',
                },
            },
        ],
    };
    protected containerParams: any = {
        type: 'group',
        name: 'container',
        layout: {
            anchors: [0, 0, 0, 0],
            pivot: [0, 0],
            offsets: [0, 0, 0, 0],
            scale: [1, 1],
        },
        components: [
            {
                type: 'input',
                data: {
                    enable: true,
                    buttonMode: true,
                },
            },
        ],
    };
    protected descriptionContainer: IImageData = {
        name: 'description_container',
        cacheKey: 'boosteritem_desc',
        layout: {
            anchors: [0, 0, 0, 0],
            pivot: [1, 0.5],
            offsets: [150, 0, 150, 0],
            scale: [1, 1],
        },
    };
    protected descriptionParams: ITextData = {
        name: 'description',
        textLocalizationKey: '',
        layout: {
            anchors: [0.5, 0.5, 0.5, 0.5],
            pivot: [0.5, 0.1],
            offsets: [0, 0, 0, 0],
            scale: [1, 1],
        },
        bounds: [250, 110],
        style: {
            ...Game.style.getStyle('HUD_in_game.booster_desc'),
            fontSize: '26px',
        },
        align: TextAlignment.CENTER,
    };
}
